/**
 * Dados MOCADOS do status pessoal do membro (regioes `showStatus`,
 * `listStatus` e `statusViewing` do prototipo SGW v2).
 *
 * Nada aqui vai para a API. O objetivo e validar o fluxo "publico meu status →
 * a empresa ve no que estou trabalhando" com conteudo real em pt-BR.
 */
import type { StatusTone } from '@orbind/tokens'

/** Disponibilidade declarada pela pessoa — rotulo + cor, nunca so cor. */
export type Availability = {
  readonly id: string
  readonly label: string
  readonly description: string
  readonly icon: string
  readonly tone: StatusTone
  readonly color: string
}

/**
 * Sao exatamente TRES (protótipo `_shell.html:3350-3362`).
 *
 * Disponibilidade e o que a pessoa DECLARA; presenca e DERIVADA da atividade
 * (ver `derivePresence`). Nao confundir as duas: a lista mostra as duas coisas.
 */
export const AVAILABILITIES: readonly Availability[] = [
  {
    id: 'trabalho',
    label: 'Em trabalho',
    description: 'Expediente normal — pode ser chamada',
    icon: 'fas fa-briefcase',
    tone: 'success',
    color: '#19B27B',
  },
  {
    id: 'intervalo',
    label: 'Em intervalo',
    description: 'Pausa curta — volta em instantes',
    icon: 'fas fa-mug-hot',
    tone: 'warning',
    color: '#E09112',
  },
  {
    id: 'fora',
    label: 'Fora do horário de trabalho',
    description: 'Fora do expediente — o ponto de presença fica oculto',
    icon: 'fas fa-power-off',
    tone: 'neutral',
    color: 'transparent',
  },
]

export function findAvailability(id: string): Availability {
  return AVAILABILITIES.find((a) => a.id === id) ?? (AVAILABILITIES[0] as Availability)
}

/* ------------------------------------------------------------- presenca --- */

export type PresenceKind = 'ativa' | 'dormindo' | 'inativa' | 'oculta'

/** Presenca DERIVADA da atividade — nunca declarada pela pessoa. */
export type Presence = {
  readonly kind: PresenceKind
  /** Rotulo em texto: o ponto colorido nunca comunica sozinho. */
  readonly label: string
  readonly color: string
  /** "z" desenhado dentro do ponto quando a pessoa esta dormindo. */
  readonly sleeping: boolean
  /** Fora do horario o ponto some por completo. */
  readonly hidden: boolean
}

/**
 * Presenca a partir da atividade na plataforma (protótipo
 * `_shell.html:2141-2148`):
 *
 * - fora do horario (ou disponibilidade "fora") → ponto OCULTO;
 * - menos de 2 min sem atividade → ativa;
 * - de 2 a 5 min → dormindo (ponto verde com "z");
 * - mais de 5 min → inativa, citando quando foi vista pela ultima vez.
 *
 * `activityMinutes` nulo significa "sem sinal" e cai no caso inativa.
 */
export function derivePresence(person: {
  readonly availabilityId: string
  readonly activityMinutes: number | null
  readonly offHours: boolean
  readonly lastSeen: string | null
}): Presence {
  if (person.offHours || person.availabilityId === 'fora') {
    return { kind: 'oculta', label: 'Fora do horário de trabalho', color: 'transparent', sleeping: false, hidden: true }
  }
  const minutos = person.activityMinutes ?? 99
  if (minutos < 2) {
    return { kind: 'ativa', label: 'Ativa na plataforma', color: '#19B27B', sleeping: false, hidden: false }
  }
  if (minutos <= 5) {
    return {
      kind: 'dormindo',
      label: `Dormindo · sem atividade há ${minutos} min`,
      color: '#19B27B',
      sleeping: true,
      hidden: false,
    }
  }
  return {
    kind: 'inativa',
    label: `Inativa há mais de 5 min${person.lastSeen ? ` · vista por último ${person.lastSeen}` : ''}`,
    color: '#E5484D',
    sleeping: false,
    hidden: false,
  }
}

/** Item de Trabalho resumido, como aparece nas listas do status. */
export type StatusWorkItem = {
  readonly id: string
  readonly code: string
  readonly title: string
  readonly typeIcon: string
  readonly typeLabel: string
  readonly statusLabel: string
  readonly statusTone: StatusTone
  /** Momento relativo: "há 40 min", "10:20", "14:00". */
  readonly time: string
}

export type PersonStatus = {
  readonly id: string
  readonly name: string
  readonly initials: string
  readonly role: string
  readonly area: string
  /** O que a pessoa DECLAROU (ver AVAILABILITIES). */
  readonly availabilityId: string
  /** Minutos desde a ultima atividade na plataforma — alimenta a presenca. */
  readonly activityMinutes: number | null
  /** Fora do expediente: a presenca fica oculta, mesmo com atividade recente. */
  readonly offHours: boolean
  /** "18:12", "ontem às 17:05" — citado quando a presenca esta inativa. */
  readonly lastSeen: string | null
  /** Conversa direta em `mocks/chat.ts`; `null` = ainda nao existe conversa. */
  readonly dmChannelId: string | null
  /** Texto publicado. `null` = pessoa nunca publicou / limpou o status. */
  readonly text: string | null
  /** Quando publicou: "há 25 min", "ontem às 18:12". */
  readonly publishedAt: string
  readonly working: readonly StatusWorkItem[]
  readonly doneToday: readonly StatusWorkItem[]
  readonly scheduledToday: readonly StatusWorkItem[]
}

/** Status da pessoa logada (mocado como Ana Martins, iniciais AM do protótipo). */
export const MY_STATUS: PersonStatus = {
  id: 'me',
  name: 'Ana Martins',
  initials: 'AM',
  role: 'Product Owner',
  area: 'Engenharia de Produto',
  availabilityId: 'trabalho',
  activityMinutes: 0,
  offHours: false,
  lastSeen: null,
  dmChannelId: null,
  text: 'Fechando o escopo do portal do franqueado da Farmais. Em foco na triagem até as 12h.',
  publishedAt: 'há 25 minutos',
  working: [
    {
      id: 'wi-1',
      code: 'FUN-412',
      title: 'Portal do franqueado — fluxo de primeiro acesso',
      typeIcon: 'fas fa-cube',
      typeLabel: 'Funcionalidade',
      statusLabel: 'Em andamento',
      statusTone: 'info',
      time: 'há 1h20',
    },
    {
      id: 'wi-2',
      code: 'HIS-1180',
      title: 'Como franqueado, quero acompanhar meu pedido de reposição',
      typeIcon: 'fas fa-book-open',
      typeLabel: 'História de usuário',
      statusLabel: 'Em revisão',
      statusTone: 'warning',
      time: 'há 35 min',
    },
  ],
  doneToday: [
    {
      id: 'wi-3',
      code: 'CRI-2044',
      title: 'Critério: senha temporária expira em 24 horas',
      typeIcon: 'fas fa-clipboard-check',
      typeLabel: 'Critério de Aceite',
      statusLabel: 'Concluído',
      statusTone: 'success',
      time: '09:48',
    },
  ],
  scheduledToday: [
    {
      id: 'wi-4',
      code: 'FUN-418',
      title: 'Relatório mensal de reposição por loja',
      typeIcon: 'fas fa-cube',
      typeLabel: 'Funcionalidade',
      statusLabel: 'Pronto para iniciar',
      statusTone: 'primary',
      time: '14:00',
    },
    {
      id: 'wi-5',
      code: 'TEC-903',
      title: 'Revisar contrato da API de estoque com o time do Meridiano',
      typeIcon: 'fas fa-wrench',
      typeLabel: 'Tarefa técnica',
      statusLabel: 'Pronto para iniciar',
      statusTone: 'primary',
      time: '16:30',
    },
  ],
}

/** Status publicados pelo restante da equipe, do mais recente ao mais antigo. */
export const TEAM_STATUSES: readonly PersonStatus[] = [
  {
    id: 'mariana',
    name: 'Mariana Bezerra',
    initials: 'MB',
    role: 'Coordenadora de Operações',
    area: 'Operações',
    availabilityId: 'trabalho',
    activityMinutes: 1,
    offHours: false,
    lastSeen: null,
    dmChannelId: 'ch-mariana-bezerra',
    text: 'Em comitê de arquitetura até as 11h. Depois disso respondo as dúvidas do contrato da API de estoque.',
    publishedAt: 'há 12 minutos',
    working: [
      {
        id: 'mc-1',
        code: 'TEC-887',
        title: 'Desenho da fila de sincronização com o ERP',
        typeIcon: 'fas fa-wrench',
        typeLabel: 'Tarefa técnica',
        statusLabel: 'Em andamento',
        statusTone: 'info',
        time: 'há 2h',
      },
    ],
    doneToday: [
      {
        id: 'mc-2',
        code: 'TEC-881',
        title: 'Definir política de retentativa do webhook do Datacenter',
        typeIcon: 'fas fa-wrench',
        typeLabel: 'Tarefa técnica',
        statusLabel: 'Concluído',
        statusTone: 'success',
        time: '08:55',
      },
    ],
    scheduledToday: [],
  },
  {
    id: 'tiago',
    name: 'Tiago Menezes',
    initials: 'TM',
    role: 'Pessoa desenvolvedora',
    area: 'Engenharia de Produto',
    availabilityId: 'trabalho',
    activityMinutes: 3,
    offHours: false,
    lastSeen: null,
    dmChannelId: null,
    text: 'Cabeça na correção do defeito de duplicidade de pedidos. Não me chame antes das 11h30, por favor.',
    publishedAt: 'há 40 minutos',
    working: [
      {
        id: 'tm-1',
        code: 'DEF-2210',
        title: 'Pedido duplicado quando a loja reenvia o formulário',
        typeIcon: 'fas fa-bug',
        typeLabel: 'Defeito',
        statusLabel: 'Em andamento',
        statusTone: 'info',
        time: 'há 3h10',
      },
      {
        id: 'tm-2',
        code: 'TEC-905',
        title: 'Índice único de idempotência na tabela de pedidos',
        typeIcon: 'fas fa-wrench',
        typeLabel: 'Tarefa técnica',
        statusLabel: 'Em andamento',
        statusTone: 'info',
        time: 'há 50 min',
      },
    ],
    doneToday: [],
    scheduledToday: [
      {
        id: 'tm-3',
        code: 'DEF-2199',
        title: 'Erro 500 ao anexar nota fiscal acima de 10 MB',
        typeIcon: 'fas fa-bug',
        typeLabel: 'Defeito',
        statusLabel: 'Pronto para iniciar',
        statusTone: 'primary',
        time: '15:00',
      },
    ],
  },
  {
    id: 'camila',
    name: 'Camila Nogueira',
    initials: 'CN',
    role: 'Coordenadora de Atendimento',
    area: 'Atendimento ao Empresa',
    availabilityId: 'trabalho',
    activityMinutes: 0,
    offHours: false,
    lastSeen: null,
    dmChannelId: null,
    text: 'Fila de triagem zerada. Disponível para apoiar quem precisar priorizar chamados hoje.',
    publishedAt: 'há 1 hora',
    working: [
      {
        id: 'cn-1',
        code: 'DEF-2214',
        title: 'Empresa Vega não recebe o e-mail de confirmação de matrícula',
        typeIcon: 'fas fa-bug',
        typeLabel: 'Defeito',
        statusLabel: 'Aguardanda empresa',
        statusTone: 'warning',
        time: 'há 25 min',
      },
    ],
    doneToday: [
      {
        id: 'cn-2',
        code: 'DEF-2207',
        title: 'Ajustar texto do aviso de senha expirada',
        typeIcon: 'fas fa-bug',
        typeLabel: 'Defeito',
        statusLabel: 'Concluído',
        statusTone: 'success',
        time: '10:12',
      },
      {
        id: 'cn-3',
        code: 'MEL-341',
        title: 'Agrupar chamados do mesmo domínio na triagem',
        typeIcon: 'fas fa-arrow-up',
        typeLabel: 'Melhoria',
        statusLabel: 'Concluído',
        statusTone: 'success',
        time: '09:30',
      },
    ],
    scheduledToday: [],
  },
  {
    id: 'rafael',
    name: 'Rafael Andrade',
    initials: 'RA',
    role: 'Especialista de Infraestrutura',
    area: 'Infraestrutura',
    availabilityId: 'intervalo',
    activityMinutes: 4,
    offHours: false,
    lastSeen: '10:05',
    dmChannelId: 'ch-rafael-andrade',
    text: 'Café rápido. Volto para a revisão dos ambientes de homologação em 10 minutos.',
    publishedAt: 'há 2 horas',
    working: [
      {
        id: 'rb-1',
        code: 'TEC-912',
        title: 'Preparar o ambiente de homologação da virada do JK Iguatemi',
        typeIcon: 'fas fa-wrench',
        typeLabel: 'Tarefa técnica',
        statusLabel: 'Em revisão',
        statusTone: 'warning',
        time: 'há 1h05',
      },
    ],
    doneToday: [],
    scheduledToday: [
      {
        id: 'rb-2',
        code: 'MEL-352',
        title: 'Alertas de disco cheio nos servidores de homologação',
        typeIcon: 'fas fa-arrow-up',
        typeLabel: 'Melhoria',
        statusLabel: 'Pronto para iniciar',
        statusTone: 'primary',
        time: '13:30',
      },
    ],
  },
  {
    id: 'juliana',
    name: 'Juliana Prado',
    initials: 'JP',
    role: 'Líder de Dados',
    area: 'Dados e Analytics',
    availabilityId: 'trabalho',
    activityMinutes: 12,
    offHours: false,
    lastSeen: '09:58',
    dmChannelId: null,
    text: 'Painel de fluxo do trimestre publicado. Aceito pedidos de recorte por área.',
    publishedAt: 'há 3 horas',
    working: [
      {
        id: 'jp-1',
        code: 'FUN-399',
        title: 'Recorte de lead time por tipo de Item de Trabalho',
        typeIcon: 'fas fa-cube',
        typeLabel: 'Funcionalidade',
        statusLabel: 'Em andamento',
        statusTone: 'info',
        time: 'há 4h',
      },
    ],
    doneToday: [
      {
        id: 'jp-2',
        code: 'TEC-898',
        title: 'Materializar a view de throughput semanal',
        typeIcon: 'fas fa-wrench',
        typeLabel: 'Tarefa técnica',
        statusLabel: 'Concluído',
        statusTone: 'success',
        time: '08:20',
      },
    ],
    scheduledToday: [],
  },
  {
    id: 'beatriz',
    name: 'Beatriz Sampaio',
    initials: 'BS',
    role: 'Analista de Atendimento',
    area: 'Atendimento ao Empresa',
    availabilityId: 'intervalo',
    activityMinutes: 2,
    offHours: false,
    lastSeen: null,
    dmChannelId: null,
    text: 'Em treinamento da nova base de conhecimento até as 14h.',
    publishedAt: 'hoje às 08:05',
    working: [],
    doneToday: [],
    scheduledToday: [
      {
        id: 'bs-1',
        code: 'DEF-2216',
        title: 'Anexos do chamado da AgroSul não abrem no celular',
        typeIcon: 'fas fa-bug',
        typeLabel: 'Defeito',
        statusLabel: 'Pronto para iniciar',
        statusTone: 'primary',
        time: '14:15',
      },
    ],
  },
  {
    id: 'diego',
    name: 'Diego Fontenele',
    initials: 'DF',
    role: 'Executivo de Contas',
    area: 'Comercial',
    availabilityId: 'trabalho',
    activityMinutes: 47,
    offHours: false,
    lastSeen: '09:23',
    dmChannelId: null,
    text: 'Reunião de renovação com o Banco Meridiano. Volto ao chat depois das 16h.',
    publishedAt: 'hoje às 09:10',
    working: [
      {
        id: 'df-1',
        code: 'FUN-421',
        title: 'Proposta de evolução 2027 — Meridiano',
        typeIcon: 'fas fa-cube',
        typeLabel: 'Funcionalidade',
        statusLabel: 'Em análise',
        statusTone: 'neutral',
        time: 'há 6h',
      },
    ],
    doneToday: [],
    scheduledToday: [],
  },
  {
    id: 'sonia',
    name: 'Sônia Vasques',
    initials: 'SV',
    role: 'Analista Financeira',
    area: 'Financeiro',
    availabilityId: 'fora',
    activityMinutes: null,
    offHours: true,
    lastSeen: 'ontem às 18:05',
    dmChannelId: null,
    text: 'Encerrei o fechamento de julho. Volto amanhã às 8h.',
    publishedAt: 'ontem às 18:12',
    working: [],
    doneToday: [],
    scheduledToday: [],
  },
]
